import type { CustomPropDefinitionAndValue } from "@v1/components/editor/page/element-editor/components/config-menus/utils";
import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { Component } from "schemas/component";

import * as React from "react";

import { Group } from "@v1/components/common/designSystem/Group";
import { getCustomComponentPropTypeEditorData } from "@v1/components/editor/customComponentProps";
import {
  getExtraActionsBasedOnComponentType,
  getSelectableOptionValues,
} from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useStoreProductsFromDraftElement } from "@v1/hooks/useStoreProducts";
import { selectLocaleData } from "@v1/reducers/commerce-reducer";
import {
  selectAllTextComponentNames,
  selectDraftComponentContext,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { useEditorSelector } from "@v1/store/store";

type StandardCustomPropControlProps = {
  draftComponent: Component;
  // NOTE (Evan, 7/25/23) customProp is optional here to make usage simpler –
  // TypeScript isn't smart enough to know that every custom prop will be defined,
  // so doing .find() on the array of CustomProps will potentially return undefined.
  // It's cleaner to do the check here rather than in the config menu that uses this
  // component.
  customProp?: CustomPropDefinitionAndValue;
  isEnabled?: boolean;
  controlledOnChange?: (value: any) => void;
  noWrapper?: boolean;
  description?: string;
};

const StandardCustomPropControl: React.FC<StandardCustomPropControlProps> = ({
  draftComponent,
  customProp,
  isEnabled = true,
  controlledOnChange,
  noWrapper,
  description,
}) => {
  const context = useEditorSelector(selectDraftComponentContext);
  const allTextComponents = useEditorSelector(selectAllTextComponentNames);
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);
  const { products } = useStoreProductsFromDraftElement();
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  const applyComponentAction = useApplyComponentAction();

  if (!customProp) {
    return null;
  }

  const editorData = getCustomComponentPropTypeEditorData(
    customProp.definition.type,
  );

  const onChange =
    controlledOnChange ??
    ((value: any) => {
      const actions: UseApplyComponentActionType[] = [
        {
          componentId: draftComponent.id,
          type: "setProps",
          value: { [customProp.definition.id]: value },
        },
      ];
      const extraActionsBasedOnComponentType =
        getExtraActionsBasedOnComponentType(
          value,
          draftComponent,
          customProp.definition,
        );

      applyComponentAction({
        type: "applyCompositeAction",
        value: [...actions, ...extraActionsBasedOnComponentType],
      });
    });

  const onDelete = () => {
    applyComponentAction({
      type: "deleteProps",
      propName: customProp.definition.id,
    });
  };

  const options = getSelectableOptionValues({
    customPropDefinition: customProp.definition,
    draftComponent,
    context,
    productResolutionDependencies: {
      products,
      moneyFormat,
      moneyWithCurrencyFormat,
      currencyCode: activeCurrency,
      language: activeLanguage,
      templateProduct,
      isEditor: true,
      isShopifyProductsLoading: false,
    },
    allTextComponents,
  });

  const contentToRender = editorData?.render({
    component: draftComponent,
    products,
    value: customProp.value,
    customPropId: customProp.definition.id,
    description: description ?? customProp.definition.description ?? "",
    onChange,
    onDelete,
    options,
    attributeName: customProp.definition.id,
    isEnabled,
    disabledDescription: customProp.definition.disabledDescription,
    definition: customProp.definition,
    context,
    disableDynamicData: customProp.definition.disableDynamicData,
    templateProduct,
    placeholder: customProp.definition.placeholder,
  });

  if (editorData.hasCustomWrapper || noWrapper) {
    return <div className="w-full">{contentToRender}</div>;
  }

  return (
    <Group
      name={customProp.definition.name}
      isDefaultOpen
      isCollapsible
      key={customProp.definition.id}
    >
      <div className="w-full">{contentToRender}</div>
    </Group>
  );
};

export default StandardCustomPropControl;
